import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, ImageIcon, Plus, Trash2, X } from 'lucide-react'
import { AssetMediaRef } from '@digital-twins/shared-types'

import { CreativeMedia } from '@/components/chat/CreativeMedia'
import { MatrixColumnScale } from '@/components/MatrixGrid/MatrixColumnScale'
import { MatrixGrid } from '@/components/MatrixGrid/MatrixGrid'
import { MatrixHeaderField } from '@/components/MatrixGrid/MatrixHeaderField'
import { MatrixGridCellOption } from '@/components/MatrixGrid/types'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import {
  pricingInlineFieldClass,
  pricingMultilineFieldClass,
} from '@/components/quick-study-results/pricingFieldStyles'
import {
  gateVersionsFromStimulusRefs,
  stimulusPublicUrl,
} from '../stimulusRefsFromRun'

import {
  addGateQuestion,
  addGateQuestionChoice,
  addGateQuestionColumn,
  addGateQuestionRow,
  canAddGateQuestion,
  GateTypeMemory,
  removeGateQuestion,
  removeGateQuestionChoice,
  removeGateQuestionColumn,
  removeGateQuestionRow,
  setGateQuestionChoice,
  setGateQuestionColumnLabel,
  setGateQuestionColumnScaleLabel,
  setGateQuestionColumnSpan,
  setGateQuestionPrompt,
  setGateQuestionRowLabel,
  setGateQuestionScaleLabel,
  setGateQuestionScaleSpan,
  setGateQuestionType,
  TWIN_TEST_MAX_QUESTIONS,
} from './gateQuestionEdits'
import {
  gateQuestionTypeLabel,
  reindexGateTypeMemory,
  TWIN_TEST_GATE_QUESTION_TYPES,
  TWIN_TEST_GATE_RATING_SPANS,
  typeNeedsColumns,
} from './twinTestGateAdapter'
import {
  GateQuestion,
  GateQuestionColumn,
  GateQuestionRow,
  GateQuestionScale,
  GateQuestionVersion,
} from './gateArtifacts'

/**
 * The drafted questions at a gate, as the reviewer needs to read — and edit —
 * them.
 *
 * WHAT THIS EXISTS TO FIX. These arrived as one collapsed row per draft with a
 * truncated first line, which asks somebody to approve spending on questions
 * they cannot actually read. So: every prompt in full, the scale it will be
 * answered on with both anchors, the options where the question has them, and
 * the versions it will be asked across.
 *
 * FIELD EDIT, NOT JSON. When `isEditable`, prompt / title / choices / scale
 * labels / type / rating span are commit-on-blur (or click) controls. Each
 * change patches the artifact body via `gateQuestionEdits` →
 * `twinTestGateQuestionAdapter` and stages through `onBodyChange`. Peer
 * add/remove respects the 3-question cap; empty list is allowed. Type-switch
 * memory is a session Map keyed by question index (studies behavior).
 *
 * THE OPTIONS ARE THE INSTRUMENT, and the versions are the bill. For a
 * single_select, a multi_select or a ranking, most of the question-writing
 * craft is in whether the answer list is balanced and exhaustive, and a
 * reviewer shown only the prompt cannot see any of it. Versions are worse than
 * invisible: they are the stimulus a panel is actually exposed to, and each one
 * is a separate pass over the panel, so they are what multiplies the number on
 * the spend line.
 *
 * THEY ARE NOT VARIANTS. Nothing here is being compared against anything else;
 * a twin test explores several angles in sequence, and each answer is input to
 * the next. The cards are numbered so the reviewer can refer to one, and the
 * word "variant" appears nowhere.
 *
 * VISUAL LANGUAGE, BORROWED NOT INVENTED. Hand-built in the gate idiom — do not
 * extract private studies `TypePicker` / `ChoiceEditor` / `RatingEditor`.
 */

/**
 * THE CARD VOCABULARY, COPIED NOT IMPORTED. Every class string below is lifted
 * verbatim from the study builder's question card so a gate card and a builder
 * card of the same type read as one component. The builder's `TypePicker`,
 * `ChoiceEditor` and `RatingEditor` are module-private and coupled to the
 * questionnaire workspace hook, so importing them would drag that hook and the
 * studies API into the thread pane. Each constant names its source line; a
 * later extraction into a shared primitive is then mechanical.
 */

/** source: QuestionnaireQuestionCard.tsx L3443 (card container) */
const GATE_CARD_CLASS =
  'group relative rounded-[12px] border border-border bg-background px-5 py-4 transition-[border-color,box-shadow] duration-150 hover:border-border/80 hover:shadow-[0_2px_14px_rgba(7,18,15,0.06)] dark:bg-card'

/** source: QuestionnaireQuestionCard.tsx L3484 (Q badge) */
const GATE_Q_BADGE_CLASS =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#DCF0EB] text-[11px] font-normal leading-none text-[#146558] dark:bg-primary/10 dark:text-primary'

/** source: QuestionnaireQuestionCard.tsx L334 (TypePicker trigger) */
const GATE_TYPE_LABEL_CLASS =
  'inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground'

/** source: QuestionnaireQuestionCard.tsx L315 (PILL_BASE_CLASS) */
const GATE_PILL_CLASS =
  'inline-flex items-center gap-1 rounded-full border px-[10px] py-[3px] text-[11px] font-medium leading-[1.4] transition-colors'

/** source: QuestionnaireQuestionCard.tsx L3729 (question title field) */
const GATE_QUESTION_TEXT_CLASS =
  'min-h-[26px] w-full resize-none rounded-[4px] border-0 bg-transparent px-1 py-0 text-[16px] font-medium leading-[1.45] tracking-normal text-foreground shadow-none transition-colors hover:bg-muted/50 focus:outline-none focus:ring-0 focus-visible:bg-primary/10 focus-visible:ring-0 dark:focus-visible:bg-accent'

/** source: QuestionnaireQuestionCard.tsx L3663 (hover-revealed icon action) */
const GATE_ICON_BUTTON_CLASS =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted-foreground opacity-0 transition-[opacity,background-color,color] hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100'

/** source: QuestionnaireQuestionCard.tsx L511 (ChoiceEditor option field) */
const GATE_QUIET_FIELD_CLASS =
  'h-auto min-h-[22px] w-full border-0 bg-transparent px-0 py-0 text-[13.5px] leading-[1.5] text-foreground shadow-none focus:outline-none focus:ring-0 focus-visible:ring-0'

/** source: QuestionnaireQuestionCard.tsx L497 (ChoiceEditor row) */
const GATE_CHOICE_ROW_CLASS =
  'group/choice flex items-center gap-2.5 rounded-[6px] px-1 py-1 transition-colors hover:bg-muted/40 dark:hover:bg-white/5'

/** source: QuestionnaireQuestionCard.tsx L499 (ChoiceEditor glyph) */
const GATE_CHOICE_GLYPH_CLASS =
  'h-3.5 w-3.5 shrink-0 border-[1.5px] border-border bg-transparent'

/** source: QuestionnaireQuestionCard.tsx L1274 (RatingEditor number box, as a rank index) */
const GATE_RANK_INDEX_CLASS =
  'flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] bg-muted text-[11px] font-medium tabular-nums text-muted-foreground'

/** source: QuestionnaireQuestionCard.tsx L537 (ChoiceEditor remove) */
const GATE_CHOICE_REMOVE_CLASS =
  'flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[4px] text-muted-foreground/0 transition-[opacity,background-color,color] hover:bg-muted hover:text-foreground group-hover/choice:text-muted-foreground group-focus-within/choice:text-muted-foreground disabled:!text-transparent'

/** source: QuestionnaireQuestionCard.tsx L1143 (RatingEditor segmented toggle shell) */
const GATE_SEGMENT_GROUP_CLASS =
  'inline-flex items-center rounded-[6px] border border-border bg-muted/40 p-0.5'

/** source: QuestionnaireQuestionCard.tsx L1117 (RatingEditor segmentButtonClass) */
const GATE_SEGMENT_CLASS =
  'rounded-[4px] px-2 py-0.5 text-[11px] font-medium transition-colors'
const GATE_SEGMENT_ACTIVE_CLASS = 'bg-background text-foreground shadow-sm dark:bg-card'
const GATE_SEGMENT_IDLE_CLASS = 'text-muted-foreground hover:text-foreground'

/** source: QuestionnaireQuestionCard.tsx L1274 (RatingEditor point box) */
const GATE_SCALE_POINT_CLASS =
  'flex h-10 flex-1 items-center justify-center rounded-[6px] border border-border bg-background text-[13px] font-medium text-muted-foreground dark:bg-card'

/** source: QuestionnaireQuestionCard.tsx L1215 (RatingEditor anchorInputClass) */
const GATE_ANCHOR_INPUT_CLASS =
  'h-auto border-0 bg-transparent px-0 py-0 text-[11px] text-muted-foreground shadow-none focus:outline-none focus:ring-0 focus-visible:ring-0'

/** source: QuestionnaireQuestionCard.tsx L3420 (open-end answer box) */
const GATE_EMPTY_ANSWER_CLASS =
  'mt-2.5 min-h-[44px] rounded-[8px] border border-border/70 bg-transparent p-3'

/** Above this, a scale reads as a range rather than as a row of cells. */
const MAX_RENDERED_SCALE_POINTS = 11

/**
 * Whether a column's `min`/`max` describe a real scale worth rendering as a
 * range. Both must be present, finite, integer, and `max` must exceed `min` —
 * a reversed pair ({min: 5, max: 1}) or a fractional bound ({min: 1, max:
 * 5.5}) is not a span the artifact actually declared, so it is treated the
 * same as no bounds at all rather than printed as though it were one.
 */
function usableGateScaleBounds(column: {
  min: number | null
  max: number | null
}): { min: number; max: number } | null {
  const { min, max } = column
  if (
    typeof min !== 'number' ||
    typeof max !== 'number' ||
    !Number.isInteger(min) ||
    !Number.isInteger(max) ||
    max <= min
  ) {
    return null
  }
  return { min, max }
}

const GATE_COLUMN_SCALE_PICKER_OPTIONS: Array<{ id: string; label: string }> =
  TWIN_TEST_GATE_RATING_SPANS.map((span) => ({
    id: `${span.min}:${span.max}`,
    label: span.label,
  }))

/**
 * Chip copy for one matrix_rating column. Every bounds read goes through
 * `usableGateScaleBounds` so a reversed pair never prints as "5-1".
 */
function gateColumnScalePicker(
  column: { min: number | null; max: number | null },
  setScaleLabel: string,
): { pickerLabel: string; selectedOptionId: string | undefined } {
  const bounds = usableGateScaleBounds(column)
  if (!bounds) {
    return { pickerLabel: setScaleLabel, selectedOptionId: undefined }
  }
  const knownSpan = TWIN_TEST_GATE_RATING_SPANS.find(
    (candidate) => candidate.min === bounds.min && candidate.max === bounds.max,
  )
  if (!knownSpan) {
    return { pickerLabel: setScaleLabel, selectedOptionId: undefined }
  }
  return {
    pickerLabel: knownSpan.label,
    selectedOptionId: `${knownSpan.min}:${knownSpan.max}`,
  }
}

/**
 * Cell options for one `matrix_rating` column, matching `TWIN_TEST_GATE_RATING_SPANS`
 * (derived from `RATING_UNIT_REGISTRY` only — continuous ids never reach here).
 * They are every integer point, as the quick-study compile asks respondents
 * (`rateMin`..`rateMax`, step 1; a 0-100 column is 101 points, not 11 steps).
 *
 * 1. <=11 raw points -> every integer point.
 * 2. Otherwise -> no options; `MatrixGrid` shows the range as text instead of
 *    mounting up to 101 `<option>` nodes.
 *
 * Invalid bounds (reversed, non-integer, or missing) never reach step 1-2:
 * they resolve to no options, same as an absent scale.
 *
 * The first/last option is labeled with `minLabel`/`maxLabel` when present.
 */
function gateMatrixColumnOptions(column: {
  min: number | null
  max: number | null
  minLabel: string | null
  maxLabel: string | null
}): Array<MatrixGridCellOption> {
  const { minLabel, maxLabel } = column
  const bounds = usableGateScaleBounds(column)
  if (!bounds) {
    return []
  }
  const { min, max } = bounds

  if (max - min + 1 > MAX_RENDERED_SCALE_POINTS) {
    return []
  }
  const points = Array.from(
    { length: max - min + 1 },
    (_unused, offset) => min + offset,
  )

  return points.map((point, index) => {
    const isFirst = index === 0
    const isLast = index === points.length - 1
    const anchor = isFirst ? minLabel : isLast ? maxLabel : null
    return {
      value: String(point),
      label: anchor ? `${point} (${anchor})` : String(point),
    }
  })
}

interface GateInlineEditProps {
  value: string
  onChange: (value: string) => void
  multiline?: boolean
  placeholder?: string
  className?: string
  dataTestId: string
  ariaLabel: string
}

/** Commit-on-blur inline field — same model as ConfirmRunCard's SpecInlineEdit. */
function GateInlineEdit(props: GateInlineEditProps) {
  const {
    value,
    onChange,
    multiline = false,
    placeholder = '',
    className,
    dataTestId,
    ariaLabel,
  } = props

  const [draft, setDraft] = useState(value)
  const isFocusedRef = useRef(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDraft(value)
    }
  }, [value])

  useEffect(() => {
    if (!multiline || !textareaRef.current) {
      return
    }
    const el = textareaRef.current
    el.style.height = '0px'
    el.style.height = `${el.scrollHeight}px`
  }, [draft, value, multiline])

  const fieldCn = cn(
    multiline ? pricingMultilineFieldClass : pricingInlineFieldClass,
    className,
  )

  if (multiline) {
    return (
      <textarea
        ref={textareaRef}
        value={draft}
        placeholder={placeholder}
        rows={1}
        aria-label={ariaLabel}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={() => {
          isFocusedRef.current = true
        }}
        onBlur={() => {
          isFocusedRef.current = false
          const next = draft.trim()
          // Compare to the prop value, not a prior draft: a no-op blur must
          // not call onChange (stringify round-trip alone can stage a phantom).
          if (next !== value) {
            onChange(next)
          }
        }}
        className={cn('mt-2 block w-full resize-none overflow-hidden leading-6', fieldCn)}
        data-testid={dataTestId}
      />
    )
  }

  return (
    <input
      type="text"
      value={draft}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(event) => setDraft(event.target.value)}
      onFocus={() => {
        isFocusedRef.current = true
      }}
      onBlur={() => {
        isFocusedRef.current = false
        const next = draft.trim()
        if (next !== value) {
          onChange(next)
        }
      }}
      className={fieldCn}
      data-testid={dataTestId}
    />
  )
}

interface GateTypePickerProps {
  questionType: string | null
  onChange: (nextType: string) => void
  dataTestId: string
  /** Appended to the trigger label, e.g. " · 1-5" on a rating. */
  labelSuffix?: string
}

/** Hand-built type picker in the gate idiom (not studies TypePicker extract). */
function GateTypePicker(props: GateTypePickerProps) {
  const { questionType, onChange, dataTestId, labelSuffix = '' } = props
  const { t } = useTranslation('app')
  const [isOpen, setIsOpen] = useState(false)
  const current = questionType ?? 'open_end'

  return (
    <span className="relative inline-flex shrink-0">
      <button
        type="button"
        className={GATE_TYPE_LABEL_CLASS}
        onClick={() => setIsOpen((open) => !open)}
        data-testid={dataTestId}
        aria-label={t('threads.detail.gate.questionType')}
      >
        {`${gateQuestionTypeLabel(current)}${labelSuffix}`}
        <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
      </button>
      {isOpen ? (
        <div
          className="absolute left-0 top-6 z-30 w-52 rounded-[8px] border border-border bg-background p-1.5 shadow-[0_6px_24px_rgba(7,18,15,0.10)]"
          data-testid={`${dataTestId}-menu`}
        >
          {TWIN_TEST_GATE_QUESTION_TYPES.map((typeId) => (
            <button
              key={typeId}
              type="button"
              className="flex w-full items-center justify-between rounded-[6px] px-2.5 py-[7px] text-left text-[13px] text-foreground transition-colors hover:bg-muted"
              onClick={() => {
                onChange(typeId)
                setIsOpen(false)
              }}
              data-testid={`${dataTestId}-option-${typeId}`}
            >
              <span>{gateQuestionTypeLabel(typeId)}</span>
              {typeId === current ? <Check className="h-3.5 w-3.5" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  )
}

interface GateQuestionScaleRowProps {
  scale: GateQuestionScale
  testId: string
  isEditable: boolean
  onMinLabelChange?: (label: string) => void
  onMaxLabelChange?: (label: string) => void
  onSpanChange?: (min: number, max: number) => void
  minLabelTestId?: string
  maxLabelTestId?: string
  spanTestId?: string
}

function GateQuestionScaleRow(props: GateQuestionScaleRowProps) {
  const {
    scale,
    testId,
    isEditable,
    onMinLabelChange,
    onMaxLabelChange,
    onSpanChange,
    minLabelTestId,
    maxLabelTestId,
    spanTestId,
  } = props

  const { t } = useTranslation('app')

  const span = Math.floor(scale.max) - Math.ceil(scale.min) + 1
  const points =
    span >= 2 && span <= MAX_RENDERED_SCALE_POINTS
      ? Array.from({ length: span }, (_unused, offset) => Math.ceil(scale.min) + offset)
      : null

  const spanKey = `${scale.min}:${scale.max}`
  const knownSpan = TWIN_TEST_GATE_RATING_SPANS.find(
    (candidate) => candidate.min === scale.min && candidate.max === scale.max,
  )

  const spanContainerTestId = spanTestId ?? `${testId}-span`

  const spanControl =
    isEditable && onSpanChange ? (
      <div
        className={cn(GATE_SEGMENT_GROUP_CLASS, 'mb-2 flex-wrap gap-0.5')}
        role="group"
        aria-label={t('threads.detail.gate.ratingScaleRange')}
        data-testid={spanContainerTestId}
        data-span={knownSpan ? spanKey : undefined}
      >
        {TWIN_TEST_GATE_RATING_SPANS.map((candidate) => {
          const isActive =
            candidate.min === scale.min && candidate.max === scale.max
          return (
            <button
              key={candidate.unitId}
              type="button"
              className={cn(
                GATE_SEGMENT_CLASS,
                isActive ? GATE_SEGMENT_ACTIVE_CLASS : GATE_SEGMENT_IDLE_CLASS,
              )}
              aria-pressed={isActive}
              onClick={() => {
                if (Number.isFinite(candidate.min) && Number.isFinite(candidate.max)) {
                  onSpanChange(candidate.min, candidate.max)
                }
              }}
              title={candidate.label}
              data-testid={`${spanContainerTestId}-${candidate.unitId}`}
            >
              {`${candidate.min}-${candidate.max}`}
            </button>
          )
        })}
      </div>
    ) : null

  const anchors =
    isEditable && onMinLabelChange && onMaxLabelChange ? (
      <div className="mt-2 flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <GateInlineEdit
            value={scale.minLabel ?? ''}
            onChange={onMinLabelChange}
            placeholder={t('threads.detail.gate.minLabel')}
            ariaLabel={t('threads.detail.gate.scaleMinAria')}
            dataTestId={minLabelTestId ?? `${testId}-min-label`}
            className={cn(GATE_ANCHOR_INPUT_CLASS, 'text-left')}
          />
        </div>
        <div className="min-w-0 flex-1">
          <GateInlineEdit
            value={scale.maxLabel ?? ''}
            onChange={onMaxLabelChange}
            placeholder={t('threads.detail.gate.maxLabel')}
            ariaLabel={t('threads.detail.gate.scaleMaxAria')}
            dataTestId={maxLabelTestId ?? `${testId}-max-label`}
            className={cn(GATE_ANCHOR_INPUT_CLASS, 'text-right')}
          />
        </div>
      </div>
    ) : scale.minLabel || scale.maxLabel ? (
      <div className="mt-2 flex items-center gap-4 text-[11px] text-muted-foreground">
        <span className="flex-1 text-left">{scale.minLabel}</span>
        <span className="flex-1 text-right">{scale.maxLabel}</span>
      </div>
    ) : null

  return (
    <div className="mt-2.5" data-testid={testId}>
      {spanControl}
      {points ? (
        <div className="flex gap-1.5">
          {points.map((point) => (
            <span key={point} className={GATE_SCALE_POINT_CLASS}>
              {point}
            </span>
          ))}
        </div>
      ) : (
        <Text size="xs" color="muted" className="leading-5">
          {t('threads.detail.gate.scaleRange', {
            min: scale.min,
            max: scale.max,
          })}
        </Text>
      )}
      {anchors}
    </div>
  )
}

/** Which answer glyph precedes a choice row, by question type. */
type GateChoiceGlyph = 'radio' | 'checkbox' | 'rank'

function gateChoiceGlyph(questionType: string | null): GateChoiceGlyph {
  if (questionType === 'multi_select') {
    return 'checkbox'
  }
  if (questionType === 'ranking') {
    return 'rank'
  }
  return 'radio'
}

interface GateQuestionChoiceListProps {
  choices: Array<string>
  /** Picks the row glyph: round, square, or the 1-based rank box. */
  glyph: GateChoiceGlyph
  testId: string
  /** Namespaces each option's own `data-testid`. */
  itemTestIdPrefix: string
  isEditable: boolean
  onChoiceChange?: (choiceIndex: number, value: string) => void
  onChoiceAdd?: () => void
  onChoiceRemove?: (choiceIndex: number) => void
}

/** The answer list, in the order it will be shown. Editable when the gate is. */
function GateQuestionChoiceList(props: GateQuestionChoiceListProps) {
  const {
    choices,
    glyph,
    testId,
    itemTestIdPrefix,
    isEditable,
    onChoiceChange,
    onChoiceAdd,
    onChoiceRemove,
  } = props

  const { t } = useTranslation('app')

  return (
    <div className="mt-2.5" data-testid={testId}>
      <ul>
        {choices.map((choice, index) => (
          <li
            key={index}
            className={GATE_CHOICE_ROW_CLASS}
            data-testid={`${itemTestIdPrefix}-${index}`}
          >
            {glyph === 'rank' ? (
              <span className={GATE_RANK_INDEX_CLASS} aria-hidden="true">
                {index + 1}
              </span>
            ) : (
              <span
                className={cn(
                  GATE_CHOICE_GLYPH_CLASS,
                  glyph === 'radio' ? 'rounded-full' : 'rounded-[3px]',
                )}
                aria-hidden="true"
              />
            )}
            {isEditable && onChoiceChange ? (
              <>
                <div className="min-w-0 flex-1">
                  <GateInlineEdit
                    value={choice}
                    onChange={(value) => onChoiceChange(index, value)}
                    ariaLabel={t('threads.detail.gate.choiceAria', {
                      index: index + 1,
                    })}
                    dataTestId={`${itemTestIdPrefix}-input-${index}`}
                    className={GATE_QUIET_FIELD_CLASS}
                  />
                </div>
                {onChoiceRemove ? (
                  <button
                    type="button"
                    onClick={() => onChoiceRemove(index)}
                    disabled={choices.length <= 2}
                    className={GATE_CHOICE_REMOVE_CLASS}
                    aria-label={t('threads.detail.gate.removeChoiceAria', {
                      index: index + 1,
                    })}
                    data-testid={`${itemTestIdPrefix}-remove-${index}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                ) : null}
              </>
            ) : (
              <span className="min-w-0 flex-1 text-[13.5px] leading-[1.5] text-muted-foreground">
                {choice}
              </span>
            )}
          </li>
        ))}
      </ul>
      {isEditable && onChoiceAdd ? (
        <button
          type="button"
          onClick={onChoiceAdd}
          className="mt-1 inline-flex items-center gap-1.5 self-start rounded-[4px] px-1 py-1 text-[12.5px] text-muted-foreground/60 transition-colors hover:text-muted-foreground"
          data-testid={`${itemTestIdPrefix}-add`}
        >
          <Plus className="h-3 w-3" />
          Add option
        </button>
      ) : null}
    </div>
  )
}

interface GateQuestionVersionListProps {
  versions: Array<GateQuestionVersion>
  /** Bound media refs aligned 1:1 with `versions` when this is a file compare. */
  mediaRefs?: Array<AssetMediaRef> | null
  testId: string
  countTestId: string
  itemTestIdPrefix: string
}

/**
 * The stimulus each pass exposes the panel to, with what it costs.
 *
 * "Runs N times" is the whole tie to the spend line: this question is asked
 * once per version, each pass to its own panel-sized read, which is where the
 * multiplied number on the approve button comes from. For a media compare the
 * versions ARE the bound files (`run.inputs.stimulus`), so N is the ref count.
 */
function GateQuestionVersionList(props: GateQuestionVersionListProps) {
  const { versions, mediaRefs = null, testId, countTestId, itemTestIdPrefix } =
    props

  const { t } = useTranslation('app')

  return (
    <div className="mt-2.5" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2 pb-1">
        <Text size="xs" weight="medium" color="muted" className="leading-5">
          Versions
        </Text>
        <Text
          size="xs"
          color="muted"
          className="shrink-0 leading-5"
          data-testid={countTestId}
        >
          {t('threads.detail.gate.runsTimes', { count: versions.length })}
        </Text>
      </div>
      <ul className="space-y-1.5">
        {versions.map((version, index) => {
          const mediaRef = mediaRefs?.[index] ?? null
          const mediaUrl = mediaRef ? stimulusPublicUrl(mediaRef) : null
          return (
            <li
              key={index}
              className="rounded-[6px] border border-border bg-background p-2.5 dark:bg-card"
              data-testid={`${itemTestIdPrefix}-${index}`}
            >
              <div className="flex items-start gap-2.5">
                {mediaRef ? (
                  mediaUrl ? (
                    <CreativeMedia
                      src={mediaUrl}
                      mimeType={mediaRef.mimeType}
                      alt={version.label}
                      className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
                      dataTestId={`${itemTestIdPrefix}-thumb-${index}`}
                    />
                  ) : (
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted"
                      data-testid={`${itemTestIdPrefix}-thumb-${index}`}
                    >
                      <ImageIcon
                        className="h-4 w-4 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </div>
                  )
                ) : null}
                <div className="min-w-0 flex-1">
                  <span
                    className="text-xs font-medium text-foreground"
                    data-testid={`${itemTestIdPrefix}-label-${index}`}
                  >
                    {version.label}
                  </span>
                  {/* In full, like the prompt: this is the copy the twins read. */}
                  <Text
                    size="sm"
                    color="muted"
                    className="mt-1 leading-6"
                    data-testid={`${itemTestIdPrefix}-content-${index}`}
                  >
                    {version.content}
                  </Text>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

interface GateBoundStimulusThumbProps {
  mediaRef: AssetMediaRef
  testId: string
}

/** Single bound file shown once above text versions (not a media compare). */
function GateBoundStimulusThumb(props: GateBoundStimulusThumbProps) {
  const { mediaRef, testId } = props
  const mediaUrl = stimulusPublicUrl(mediaRef)
  const label =
    typeof mediaRef.label === 'string' && mediaRef.label.trim().length > 0
      ? mediaRef.label.trim()
      : mediaRef.kind

  return (
    <div
      className="mt-2.5 flex items-center gap-2 rounded-[6px] border border-border bg-background p-2 dark:bg-card"
      data-testid={testId}
    >
      {mediaUrl ? (
        <CreativeMedia
          src={mediaUrl}
          mimeType={mediaRef.mimeType}
          alt={label}
          className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
          dataTestId={`${testId}-media`}
        />
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
          <ImageIcon
            className="h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
        </div>
      )}
      <Text size="sm" as="span" className="min-w-0 flex-1 truncate">
        {label}
      </Text>
    </div>
  )
}

/**
 * Gate row list for a matrix question.
 *
 * When the artifact carries ≥2 versions on a grid, the run replaces the authored
 * generic row with those versions as option rows. The preview must show the same
 * subjects — never the silent authored placeholder while spend/runs use V.
 */
export function resolveGateDisplayRows(args: {
  questionType: string | null
  rows: Array<GateQuestionRow> | null
  versions: Array<GateQuestionVersion> | null
}): { rows: Array<GateQuestionRow> | null; rowsFromVersions: boolean } {
  const { questionType, rows, versions } = args
  if (
    typeNeedsColumns(questionType ?? '') &&
    versions &&
    versions.length >= 2
  ) {
    return {
      rows: versions.map((version, index) => ({
        id: `version-${index}`,
        label: version.label,
      })),
      rowsFromVersions: true,
    }
  }
  return { rows, rowsFromVersions: false }
}

interface GateQuestionRowListProps {
  rows: Array<GateQuestionRow>
  testId: string
  itemTestIdPrefix: string
  isEditable: boolean
  /** When true, rows mirror versions (read-only; what the run will ask). */
  rowsFromVersions?: boolean
  onRowLabelChange?: (rowIndex: number, label: string) => void
  onRowAdd?: () => void
  onRowRemove?: (rowIndex: number) => void
}

function GateQuestionRowList(props: GateQuestionRowListProps) {
  const {
    rows,
    testId,
    itemTestIdPrefix,
    isEditable,
    rowsFromVersions = false,
    onRowLabelChange,
    onRowAdd,
    onRowRemove,
  } = props

  const { t } = useTranslation('app')

  // Version-backed rows are display-only: editing them would diverge from the
  // versions list that still owns the stimulus copy and the spend multiplier.
  const canEditRows = isEditable && !rowsFromVersions

  return (
    <div
      className="mt-2.5"
      data-testid={testId}
      data-rows-from-versions={rowsFromVersions ? 'true' : undefined}
    >
      <Text size="xs" weight="medium" color="muted" className="mb-1 leading-5">
        Rows
      </Text>
      <ul className="space-y-1">
        {rows.map((row, index) => (
          <li
            key={row.id || index}
            className={cn(
              'flex items-center gap-1.5',
              !canEditRows &&
                'rounded-[6px] border border-border bg-background px-2.5 py-1.5 text-[13px] text-muted-foreground dark:bg-card',
            )}
            data-testid={`${itemTestIdPrefix}-${index}`}
          >
            {canEditRows && onRowLabelChange ? (
              <>
                <div className="min-w-0 flex-1">
                  <GateInlineEdit
                    value={row.label}
                    onChange={(value) => onRowLabelChange(index, value)}
                    ariaLabel={t('threads.detail.gate.rowAria', {
                      index: index + 1,
                    })}
                    dataTestId={`${itemTestIdPrefix}-input-${index}`}
                    className={GATE_QUIET_FIELD_CLASS}
                  />
                </div>
                {onRowRemove ? (
                  <button
                    type="button"
                    onClick={() => onRowRemove(index)}
                    disabled={rows.length <= 1}
                    className="inline-flex shrink-0 rounded p-0.5 text-muted-foreground/70 transition-colors hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label={t('threads.detail.gate.removeRowAria', {
                      index: index + 1,
                    })}
                    data-testid={`${itemTestIdPrefix}-remove-${index}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </>
            ) : (
              row.label
            )}
          </li>
        ))}
      </ul>
      {canEditRows && onRowAdd ? (
        <button
          type="button"
          onClick={onRowAdd}
          className="mt-1 inline-flex items-center gap-1.5 self-start rounded-[4px] px-1 py-1 text-[12.5px] text-muted-foreground/60 transition-colors hover:text-muted-foreground"
          data-testid={`${itemTestIdPrefix}-add`}
        >
          <Plus className="h-3 w-3" />
          Add row
        </button>
      ) : null}
    </div>
  )
}

interface GateQuestionColumnListProps {
  columns: Array<GateQuestionColumn>
  questionType: string | null
  testId: string
  itemTestIdPrefix: string
  isEditable: boolean
  onColumnLabelChange?: (columnIndex: number, label: string) => void
  onColumnSpanChange?: (columnIndex: number, min: number, max: number) => void
  onColumnMinLabelChange?: (columnIndex: number, label: string) => void
  onColumnMaxLabelChange?: (columnIndex: number, label: string) => void
  onColumnAdd?: () => void
  onColumnRemove?: (columnIndex: number) => void
}

function GateQuestionColumnList(props: GateQuestionColumnListProps) {
  const {
    columns,
    questionType,
    testId,
    itemTestIdPrefix,
    isEditable,
    onColumnLabelChange,
    onColumnSpanChange,
    onColumnMinLabelChange,
    onColumnMaxLabelChange,
    onColumnAdd,
    onColumnRemove,
  } = props

  const { t } = useTranslation('app')

  const showSpans = questionType === 'matrix_rating'
  const minColumns = questionType === 'matrix_categorical' ? 2 : 1

  return (
    <div className="mt-2.5" data-testid={testId}>
      <Text size="xs" weight="medium" color="muted" className="mb-1 leading-5">
        Columns
      </Text>
      <ul className="space-y-1.5">
        {columns.map((column, index) => {
          const spanKey = `${column.min}:${column.max}`
          const knownSpan = TWIN_TEST_GATE_RATING_SPANS.find(
            (candidate) =>
              candidate.min === column.min && candidate.max === column.max,
          )
          const columnAnchors =
            showSpans &&
            isEditable &&
            onColumnMinLabelChange &&
            onColumnMaxLabelChange ? (
              <div className="mt-1.5 flex items-start gap-4">
                <div className="min-w-0 flex-1">
                  <GateInlineEdit
                    value={column.minLabel ?? ''}
                    onChange={(value) => onColumnMinLabelChange(index, value)}
                    placeholder={t('threads.detail.gate.minLabel')}
                    ariaLabel={t('threads.detail.gate.columnMinAria', {
                      index: index + 1,
                    })}
                    dataTestId={`${itemTestIdPrefix}-min-label-${index}`}
                    className={cn(GATE_ANCHOR_INPUT_CLASS, 'text-left')}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <GateInlineEdit
                    value={column.maxLabel ?? ''}
                    onChange={(value) => onColumnMaxLabelChange(index, value)}
                    placeholder={t('threads.detail.gate.maxLabel')}
                    ariaLabel={t('threads.detail.gate.columnMaxAria', {
                      index: index + 1,
                    })}
                    dataTestId={`${itemTestIdPrefix}-max-label-${index}`}
                    className={cn(GATE_ANCHOR_INPUT_CLASS, 'text-right')}
                  />
                </div>
              </div>
            ) : showSpans && (column.minLabel || column.maxLabel) ? (
              <div className="mt-1.5 flex items-start gap-4 text-[11px] text-muted-foreground">
                <span className="flex-1 text-left">{column.minLabel}</span>
                <span className="flex-1 text-right">{column.maxLabel}</span>
              </div>
            ) : null
          return (
            <li
              key={column.id || index}
              className="rounded-[6px] border border-border bg-background p-2 dark:bg-card"
              data-testid={`${itemTestIdPrefix}-${index}`}
            >
              <div className="flex items-center gap-1.5">
                {isEditable && onColumnLabelChange ? (
                  <>
                    <div className="min-w-0 flex-1">
                      <GateInlineEdit
                        value={column.label}
                        onChange={(value) => onColumnLabelChange(index, value)}
                        ariaLabel={t('threads.detail.gate.columnAria', {
                          index: index + 1,
                        })}
                        dataTestId={`${itemTestIdPrefix}-input-${index}`}
                        className={GATE_QUIET_FIELD_CLASS}
                      />
                    </div>
                    {onColumnRemove ? (
                      <button
                        type="button"
                        onClick={() => onColumnRemove(index)}
                        disabled={columns.length <= minColumns}
                        className="inline-flex shrink-0 rounded p-0.5 text-muted-foreground/70 transition-colors hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label={t('threads.detail.gate.removeColumnAria', {
                          index: index + 1,
                        })}
                        data-testid={`${itemTestIdPrefix}-remove-${index}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </>
                ) : (
                  <span className="text-[13.5px] leading-[1.5] text-muted-foreground">
                    {column.label}
                  </span>
                )}
              </div>
              {showSpans ? (
                isEditable && onColumnSpanChange ? (
                  <select
                    value={knownSpan ? spanKey : ''}
                    onChange={(event) => {
                      const [minRaw, maxRaw] = event.target.value.split(':')
                      const min = Number(minRaw)
                      const max = Number(maxRaw)
                      if (Number.isFinite(min) && Number.isFinite(max)) {
                        onColumnSpanChange(index, min, max)
                      }
                    }}
                    className="mt-1.5 h-7 w-full rounded-[6px] border border-border bg-background px-2 text-[12px] text-foreground dark:bg-card"
                    data-testid={`${itemTestIdPrefix}-span-${index}`}
                    aria-label={t('threads.detail.gate.columnScaleAria', {
                      index: index + 1,
                    })}
                  >
                    {!knownSpan &&
                    typeof column.min === 'number' &&
                    typeof column.max === 'number' ? (
                      <option value="" disabled>
                        {`${column.min}–${column.max}`}
                      </option>
                    ) : null}
                    {TWIN_TEST_GATE_RATING_SPANS.map((candidate) => (
                      <option
                        key={candidate.unitId}
                        value={`${candidate.min}:${candidate.max}`}
                      >
                        {candidate.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Text size="xs" color="muted" className="mt-1 leading-5">
                    {typeof column.min === 'number' &&
                    typeof column.max === 'number'
                      ? `${column.min}–${column.max}`
                      : t('threads.detail.gate.setScale')}
                  </Text>
                )
              ) : null}
              {columnAnchors}
            </li>
          )
        })}
      </ul>
      {isEditable && onColumnAdd ? (
        <button
          type="button"
          onClick={onColumnAdd}
          className="mt-1 inline-flex items-center gap-1.5 self-start rounded-[4px] px-1 py-1 text-[12.5px] text-muted-foreground/60 transition-colors hover:text-muted-foreground"
          data-testid={`${itemTestIdPrefix}-add`}
        >
          <Plus className="h-3 w-3" />
          Add column
        </button>
      ) : null}
    </div>
  )
}

interface RunGateQuestionListProps {
  questions: Array<GateQuestion>
  /** The artifact's position in the gate list. Namespaces every `data-testid`. */
  position: number
  /** Wide + editable gate + owner: show inline fields. */
  isEditable?: boolean
  /** Staged artifact body — patched then passed to `onBodyChange`. */
  body?: string
  /** Stages into `GateEditState` (single approve still owns the wire). */
  onBodyChange?: (body: string) => void
  /**
   * Bound `run.inputs.stimulus` refs. When N≥2 the Versions list IS those
   * files (thumbs + labels). When 0–1, artifact text versions stay; a single
   * bound file may render once above the list.
   */
  stimulusRefs?: Array<AssetMediaRef>
  /**
   * Peer-question ceiling for the Add-question button, resolved by the
   * caller from the run's own template (`twinTestGateMaxQuestions`). Defaults
   * to `TWIN_TEST_MAX_QUESTIONS` when the caller has no template to read yet.
   */
  maxQuestions?: number
  /** Study / thread content language for seeded option labels. */
  language?: string | null
}

export function RunGateQuestionList(props: RunGateQuestionListProps) {
  const {
    questions,
    position,
    isEditable = false,
    body = '',
    onBodyChange,
    stimulusRefs = [],
    maxQuestions = TWIN_TEST_MAX_QUESTIONS,
    language = null,
  } = props
  const { t } = useTranslation('app')
  const isMediaCompare = stimulusRefs.length >= 2
  const mediaCompareVersions = isMediaCompare
    ? gateVersionsFromStimulusRefs(stimulusRefs)
    : null
  const singleBoundRef = stimulusRefs.length === 1 ? stimulusRefs[0] : null

  const canEdit = isEditable && Boolean(onBodyChange) && body.length > 0
  const mayAddQuestion = canEdit && canAddGateQuestion(body, maxQuestions)
  const typeMemoryRef = useRef<GateTypeMemory>(new Map())

  function commit(nextBody: string) {
    if (nextBody !== body) {
      onBodyChange?.(nextBody)
    }
  }

  const addButton = mayAddQuestion ? (
    <button
      type="button"
      onClick={() => commit(addGateQuestion(body, maxQuestions, 'open_end', language))}
      className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-background px-3 py-6 text-xs text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground dark:bg-card"
      data-testid="run-gate-question-add"
    >
      <Plus className="h-3.5 w-3.5" /> Add question
    </button>
  ) : null

  if (questions.length === 0) {
    return (
      <div
        className="space-y-2"
        data-testid={`run-gate-questions-empty-${position}`}
      >
        <div className="rounded-lg border border-dashed border-border bg-background px-3 py-8 text-center dark:bg-card">
          <Text size="sm" color="muted" className="leading-6">
            No questions yet. Add one to run this twin test.
          </Text>
        </div>
        {addButton}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <ol className="space-y-2" data-testid={`run-gate-questions-${position}`}>
      {questions.map((question, index) => {
        const compareVersions = mediaCompareVersions ?? question.versions
        const displayRows = resolveGateDisplayRows({
          questionType: question.questionType,
          rows: question.rows,
          versions: compareVersions,
        })
        const isMatrixQuestion = typeNeedsColumns(question.questionType ?? '')
        const isMatrixRating = question.questionType === 'matrix_rating'
        const matrixColumns = isMatrixQuestion
          ? (question.columns ?? []).map((column) => {
              const options = isMatrixRating ? gateMatrixColumnOptions(column) : []
              // A dropdown column with no options needs SOME text in the
              // cell instead of a blank <select>. When the bounds are usable
              // but too wide to enumerate, show the real span; otherwise
              // there is no scale to show, so the cell says the same thing
              // the header meta does (`setScale`) rather than fabricating a
              // number the artifact never declared.
              const cellPlaceholder =
                isMatrixRating && options.length === 0
                  ? usableGateScaleBounds(column)
                    ? `${column.min}-${column.max}`
                    : t('threads.detail.gate.setScale')
                  : undefined
              return {
                id: column.id,
                label: column.label,
                options,
                cellPlaceholder,
              }
            })
          : []
        // A matrix with nothing to show and nothing to add would otherwise
        // mount a bare table under the prompt (headerless, rowless). Render
        // it only when there is content, or when the author can add the
        // first row/column.
        const hasMatrixRows = Boolean(
          displayRows.rows && displayRows.rows.length > 0,
        )
        const hasMatrixColumns = matrixColumns.length > 0
        const showMatrixGrid =
          isMatrixQuestion && (hasMatrixRows || hasMatrixColumns || canEdit)

        return (
        <li
          key={index}
          className={GATE_CARD_CLASS}
          data-testid={`run-gate-question-${position}-${index}`}
        >
          {/* Q badge, what kind of question it is, and the one destructive
              action — the builder card's header, in the same order. */}
          <div className="mb-2 flex items-center gap-2">
            <span
              className={GATE_Q_BADGE_CLASS}
              data-testid={`run-gate-question-badge-${position}-${index}`}
            >
              {`Q${index + 1}`}
            </span>
            {canEdit ? (
              <GateTypePicker
                questionType={question.questionType}
                onChange={(nextType) =>
                  commit(
                    setGateQuestionType(
                      body,
                      index,
                      nextType,
                      typeMemoryRef.current,
                      language,
                    ),
                  )
                }
                labelSuffix={
                  question.questionType === 'rating' && question.scale
                    ? ` · ${question.scale.min}-${question.scale.max}`
                    : ''
                }
                dataTestId={`run-gate-question-type-${position}-${index}`}
              />
            ) : question.questionType ? (
              <span
                className={cn(
                  GATE_PILL_CLASS,
                  'shrink-0 border-transparent bg-muted capitalize text-muted-foreground',
                )}
                data-testid={`run-gate-question-type-${position}-${index}`}
              >
                {question.questionType}
              </span>
            ) : null}
            <span className="flex-1" />
            {canEdit ? (
              <button
                type="button"
                onClick={() => {
                  commit(removeGateQuestion(body, index))
                  typeMemoryRef.current = reindexGateTypeMemory(
                    typeMemoryRef.current,
                    index,
                  )
                }}
                className={GATE_ICON_BUTTON_CLASS}
                aria-label={t('threads.detail.gate.removeQuestionAria', {
                  index: index + 1,
                })}
                data-testid={`run-gate-question-remove-${position}-${index}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>

          {/* The question itself is the headline of the card. Never truncated
              and never clamped: an approver who cannot read the whole prompt is
              approving the part they can see. */}
          {canEdit ? (
            <GateInlineEdit
              value={question.prompt}
              onChange={(prompt) =>
                commit(setGateQuestionPrompt(body, index, prompt))
              }
              multiline
              placeholder={t('threads.detail.gate.questionPrompt')}
              ariaLabel={t('threads.detail.gate.questionPromptAria', {
                index: index + 1,
              })}
              dataTestId={`run-gate-question-prompt-${position}-${index}`}
              className={GATE_QUESTION_TEXT_CLASS}
            />
          ) : (
            <Text
              size="sm"
              className="mt-2 px-1 text-[16px] font-medium leading-[1.45] text-foreground"
              data-testid={`run-gate-question-prompt-${position}-${index}`}
            >
              {question.prompt}
            </Text>
          )}

          {/* What an open end looks like to answer: an empty box, never an
              input of ours — nobody types a twin's answer here. */}
          {question.questionType === 'open_end' ? (
            <div
              className={GATE_EMPTY_ANSWER_CLASS}
              aria-hidden="true"
              data-testid={`run-gate-question-answer-box-${position}-${index}`}
            />
          ) : null}

          {question.scale &&
          !typeNeedsColumns(question.questionType ?? '') ? (
            <GateQuestionScaleRow
              scale={question.scale}
              testId={`run-gate-question-scale-${position}-${index}`}
              isEditable={canEdit}
              onMinLabelChange={
                canEdit
                  ? (label) =>
                      commit(setGateQuestionScaleLabel(body, index, 'minLabel', label))
                  : undefined
              }
              onMaxLabelChange={
                canEdit
                  ? (label) =>
                      commit(setGateQuestionScaleLabel(body, index, 'maxLabel', label))
                  : undefined
              }
              onSpanChange={
                canEdit
                  ? (min, max) =>
                      commit(setGateQuestionScaleSpan(body, index, min, max))
                  : undefined
              }
              minLabelTestId={`run-gate-question-scale-min-${position}-${index}`}
              maxLabelTestId={`run-gate-question-scale-max-${position}-${index}`}
              spanTestId={`run-gate-question-scale-span-${position}-${index}`}
            />
          ) : null}

          {question.choices.length > 0 &&
          !typeNeedsColumns(question.questionType ?? '') ? (
            <GateQuestionChoiceList
              choices={question.choices}
              glyph={gateChoiceGlyph(question.questionType)}
              testId={`run-gate-question-choices-${position}-${index}`}
              itemTestIdPrefix={`run-gate-question-choice-${position}-${index}`}
              isEditable={canEdit}
              onChoiceChange={
                canEdit
                  ? (choiceIndex, value) =>
                      commit(setGateQuestionChoice(body, index, choiceIndex, value))
                  : undefined
              }
              onChoiceAdd={
                canEdit
                  ? () => commit(addGateQuestionChoice(body, index, language))
                  : undefined
              }
              onChoiceRemove={
                canEdit
                  ? (choiceIndex) =>
                      commit(removeGateQuestionChoice(body, index, choiceIndex))
                  : undefined
              }
            />
          ) : null}

          {showMatrixGrid ? (
            <div className="mt-2.5">
              <MatrixGrid
                rows={displayRows.rows ?? []}
                columns={matrixColumns}
                cellKind={isMatrixRating ? 'dropdown' : 'dot'}
                revealControlsOnHover
                disabled={!canEdit}
                minRows={1}
                minColumns={question.questionType === 'matrix_categorical' ? 2 : 1}
                canEditRows={canEdit && !displayRows.rowsFromVersions}
                canEditColumns={canEdit}
                cellAriaLabel={(row, column) =>
                  t('threads.detail.gate.cellAria', {
                    row: row.label,
                    column: column.label,
                  })
                }
                rowTestId={(rowIndex) =>
                  `run-gate-question-row-${position}-${index}-${rowIndex}`
                }
                columnTestId={(columnIndex) =>
                  `run-gate-question-column-${position}-${index}-${columnIndex}`
                }
                cellTestId={(rowIndex, columnIndex) =>
                  `run-gate-question-cell-${position}-${index}-${rowIndex}-${columnIndex}`
                }
                addRowTestId={`run-gate-question-row-${position}-${index}-add`}
                addColumnTestId={`run-gate-question-column-${position}-${index}-add`}
                removeRowTestId={(rowIndex) =>
                  `run-gate-question-row-${position}-${index}-remove-${rowIndex}`
                }
                removeColumnTestId={(columnIndex) =>
                  `run-gate-question-column-${position}-${index}-remove-${columnIndex}`
                }
                onRowAdd={
                  canEdit && !displayRows.rowsFromVersions
                    ? () => commit(addGateQuestionRow(body, index))
                    : undefined
                }
                onRowRemove={
                  canEdit && !displayRows.rowsFromVersions
                    ? (rowIndex) =>
                        commit(removeGateQuestionRow(body, index, rowIndex))
                    : undefined
                }
                onColumnAdd={
                  canEdit
                    ? () => commit(addGateQuestionColumn(body, index))
                    : undefined
                }
                onColumnRemove={
                  canEdit
                    ? (columnIndex) =>
                        commit(removeGateQuestionColumn(body, index, columnIndex))
                    : undefined
                }
                addRowLabel={t('threads.detail.gate.addRow')}
                addRowAriaLabel={t('threads.detail.gate.addRowAria')}
                addColumnAriaLabel={t('threads.detail.gate.addColumnAria')}
                removeRowAriaLabel={(rowIndex) =>
                  t('threads.detail.gate.removeRowAria', { index: rowIndex + 1 })
                }
                removeColumnAriaLabel={(columnIndex) =>
                  t('threads.detail.gate.removeColumnAria', {
                    index: columnIndex + 1,
                  })
                }
                renderRowLabel={(row, rowIndex) =>
                  canEdit && !displayRows.rowsFromVersions ? (
                    <MatrixHeaderField
                      value={row.label}
                      onChange={(value) =>
                        commit(setGateQuestionRowLabel(body, index, rowIndex, value))
                      }
                      commitMode="blur"
                      variant="row"
                      ariaLabel={t('threads.detail.gate.rowAria', {
                        index: rowIndex + 1,
                      })}
                      dataTestId={`run-gate-question-row-${position}-${index}-input-${rowIndex}`}
                    />
                  ) : (
                    row.label
                  )
                }
                renderColumnLabel={(column, columnIndex) =>
                  canEdit ? (
                    <MatrixHeaderField
                      value={column.label}
                      onChange={(value) =>
                        commit(
                          setGateQuestionColumnLabel(body, index, columnIndex, value),
                        )
                      }
                      commitMode="blur"
                      variant="column"
                      ariaLabel={t('threads.detail.gate.columnAria', {
                        index: columnIndex + 1,
                      })}
                      dataTestId={`run-gate-question-column-${position}-${index}-input-${columnIndex}`}
                    />
                  ) : (
                    column.label
                  )
                }
                renderColumnMeta={
                  isMatrixRating
                    ? (_column, columnIndex) => {
                        const gateColumn = question.columns?.[columnIndex]
                        if (!gateColumn) {
                          return null
                        }
                        const setScaleLabel = t('threads.detail.gate.setScale')
                        const { pickerLabel, selectedOptionId } =
                          gateColumnScalePicker(gateColumn, setScaleLabel)
                        const minText = gateColumn.minLabel ?? ''
                        const maxText = gateColumn.maxLabel ?? ''

                        if (!canEdit) {
                          return (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="text-[10.5px] leading-[1.3] text-muted-foreground">
                                {pickerLabel}
                              </span>
                              {minText || maxText ? (
                                <span className="flex w-full items-center gap-1 text-[10.5px] leading-[1.3] text-muted-foreground">
                                  <span className="min-w-0 flex-1 text-center">
                                    {minText}
                                  </span>
                                  <span
                                    className="shrink-0 text-muted-foreground/70"
                                    aria-hidden
                                  >
                                    →
                                  </span>
                                  <span className="min-w-0 flex-1 text-center">
                                    {maxText}
                                  </span>
                                </span>
                              ) : null}
                            </div>
                          )
                        }

                        return (
                          <MatrixColumnScale
                            pickerLabel={pickerLabel}
                            pickerOptions={GATE_COLUMN_SCALE_PICKER_OPTIONS}
                            selectedOptionId={selectedOptionId}
                            onPick={(optionId) => {
                              const [minRaw, maxRaw] = optionId.split(':')
                              const min = Number(minRaw)
                              const max = Number(maxRaw)
                              if (Number.isFinite(min) && Number.isFinite(max)) {
                                commit(
                                  setGateQuestionColumnSpan(
                                    body,
                                    index,
                                    columnIndex,
                                    min,
                                    max,
                                  ),
                                )
                              }
                            }}
                            minValue={minText}
                            maxValue={maxText}
                            onMinChange={(value) =>
                              commit(
                                setGateQuestionColumnScaleLabel(
                                  body,
                                  index,
                                  columnIndex,
                                  'min',
                                  value,
                                ),
                              )
                            }
                            onMaxChange={(value) =>
                              commit(
                                setGateQuestionColumnScaleLabel(
                                  body,
                                  index,
                                  columnIndex,
                                  'max',
                                  value,
                                ),
                              )
                            }
                            commitMode="blur"
                            minPlaceholder={t('threads.detail.gate.minLabel')}
                            maxPlaceholder={t('threads.detail.gate.maxLabel')}
                            pickerAriaLabel={t(
                              'threads.detail.gate.columnScaleAria',
                              { index: columnIndex + 1 },
                            )}
                            minAriaLabel={t('threads.detail.gate.columnMinAria', {
                              index: columnIndex + 1,
                            })}
                            maxAriaLabel={t('threads.detail.gate.columnMaxAria', {
                              index: columnIndex + 1,
                            })}
                            testId={`run-gate-question-column-${position}-${index}-span-${columnIndex}`}
                            minTestId={`run-gate-question-column-${position}-${index}-min-label-${columnIndex}`}
                            maxTestId={`run-gate-question-column-${position}-${index}-max-label-${columnIndex}`}
                          />
                        )
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              {displayRows.rows && displayRows.rows.length > 0 ? (
                <GateQuestionRowList
                  rows={displayRows.rows}
                  rowsFromVersions={displayRows.rowsFromVersions}
                  testId={`run-gate-question-rows-${position}-${index}`}
                  itemTestIdPrefix={`run-gate-question-row-${position}-${index}`}
                  isEditable={canEdit}
                  onRowLabelChange={
                    canEdit && !displayRows.rowsFromVersions
                      ? (rowIndex, label) =>
                          commit(setGateQuestionRowLabel(body, index, rowIndex, label))
                      : undefined
                  }
                  onRowAdd={
                    canEdit && !displayRows.rowsFromVersions
                      ? () => commit(addGateQuestionRow(body, index))
                      : undefined
                  }
                  onRowRemove={
                    canEdit && !displayRows.rowsFromVersions
                      ? (rowIndex) =>
                          commit(removeGateQuestionRow(body, index, rowIndex))
                      : undefined
                  }
                />
              ) : null}

              {question.columns && question.columns.length > 0 ? (
                <GateQuestionColumnList
                  columns={question.columns}
                  questionType={question.questionType}
                  testId={`run-gate-question-columns-${position}-${index}`}
                  itemTestIdPrefix={`run-gate-question-column-${position}-${index}`}
                  isEditable={canEdit}
                  onColumnLabelChange={
                    canEdit
                      ? (columnIndex, label) =>
                          commit(
                            setGateQuestionColumnLabel(body, index, columnIndex, label),
                          )
                      : undefined
                  }
                  onColumnSpanChange={
                    canEdit
                      ? (columnIndex, min, max) =>
                          commit(
                            setGateQuestionColumnSpan(
                              body,
                              index,
                              columnIndex,
                              min,
                              max,
                            ),
                          )
                      : undefined
                  }
                  onColumnMinLabelChange={
                    canEdit
                      ? (columnIndex, label) =>
                          commit(
                            setGateQuestionColumnScaleLabel(
                              body,
                              index,
                              columnIndex,
                              'min',
                              label,
                            ),
                          )
                      : undefined
                  }
                  onColumnMaxLabelChange={
                    canEdit
                      ? (columnIndex, label) =>
                          commit(
                            setGateQuestionColumnScaleLabel(
                              body,
                              index,
                              columnIndex,
                              'max',
                              label,
                            ),
                          )
                      : undefined
                  }
                  onColumnAdd={
                    canEdit
                      ? () => commit(addGateQuestionColumn(body, index))
                      : undefined
                  }
                  onColumnRemove={
                    canEdit
                      ? (columnIndex) =>
                          commit(removeGateQuestionColumn(body, index, columnIndex))
                      : undefined
                  }
                />
              ) : null}
            </>
          )}

          {singleBoundRef && !isMediaCompare ? (
            <GateBoundStimulusThumb
              mediaRef={singleBoundRef}
              testId={`run-gate-question-stimulus-${position}-${index}`}
            />
          ) : null}

          {mediaCompareVersions ? (
            <GateQuestionVersionList
              versions={mediaCompareVersions}
              mediaRefs={stimulusRefs}
              testId={`run-gate-question-versions-${position}-${index}`}
              countTestId={`run-gate-question-version-count-${position}-${index}`}
              itemTestIdPrefix={`run-gate-question-version-${position}-${index}`}
            />
          ) : question.versions ? (
            <GateQuestionVersionList
              versions={question.versions}
              testId={`run-gate-question-versions-${position}-${index}`}
              countTestId={`run-gate-question-version-count-${position}-${index}`}
              itemTestIdPrefix={`run-gate-question-version-${position}-${index}`}
            />
          ) : null}
        </li>
        )
      })}
    </ol>
      {addButton}
    </div>
  )
}
